import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import { useStyles } from "./styles";

const whiteFieldSx = {
  "& .MuiInputBase-root": {
    backgroundColor: "#fff !important",
  },
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff !important",
  },
  "& .MuiFilledInput-root": {
    backgroundColor: "#fff !important",
  },
};

const whiteInputProps = {
  sx: { backgroundColor: "#fff" },
};

const LinkButtonEditor = React.forwardRef(function LinkButtonEditor(
  {
    values,
    setFieldValue,
    setFieldError,
    createEmptyButton,
    validateButtons,
    error,
    isLimitedMode = false,
    buttonsField = "link_buttons",
    enabledField = "link_buttons_enabled",
    sectionTitle = "Button links",
    toggleLabel,
    toggleAriaLabel = "Enable button links",
    addButtonLabel = "Add button link",
    showCommentField = true,
    keyPrefix = "link",
    helperText,
    validationErrorMessage = "Each button link must have both title and URL",
  },
  ref
) {
  const classes = useStyles();
  const inputRefs = React.useRef({});
  const buttonsForView = React.useMemo(
    () => (Array.isArray(values?.[buttonsField]) ? values[buttonsField] : []),
    [buttonsField, values]
  );

  const setInputRef = React.useCallback((key, element) => {
    if (element) {
      inputRefs.current[key] = element;
      return;
    }

    delete inputRefs.current[key];
  }, []);

  const getCurrentButtons = React.useCallback(
    () =>
      buttonsForView.map((button, index) => {
        const titleRef = inputRefs.current[`title-${index}`];
        const urlRef = inputRefs.current[`url-${index}`];
        const nextButton = {
          ...button,
          title:
            titleRef && typeof titleRef.value === "string"
              ? titleRef.value
              : button?.title || "",
          url:
            urlRef && typeof urlRef.value === "string"
              ? urlRef.value
              : button?.url || "",
        };

        if (showCommentField) {
          const commentRef = inputRefs.current[`comment-${index}`];
          nextButton.comment_for_ai =
            commentRef && typeof commentRef.value === "string"
              ? commentRef.value
              : button?.comment_for_ai || "";
        } else {
          delete nextButton.comment_for_ai;
        }

        return nextButton;
      }),
    [buttonsForView, showCommentField]
  );

  const syncValidation = React.useCallback(
    (nextButtons, enabled = values?.[enabledField]) => {
      const isInvalid = validateButtons({
        ...values,
        [buttonsField]: nextButtons,
        [enabledField]: enabled,
      });

      setFieldError(
        buttonsField,
        isInvalid ? validationErrorMessage : undefined
      );
    },
    [
      buttonsField,
      enabledField,
      setFieldError,
      validateButtons,
      validationErrorMessage,
      values,
    ]
  );

  React.useImperativeHandle(
    ref,
    () => ({
      getLatestButtons: getCurrentButtons,
    }),
    [getCurrentButtons]
  );

  const updateButton = (index, nextValue) => {
    const nextButtons = getCurrentButtons();
    nextButtons[index] = {
      ...nextButtons[index],
      ...nextValue,
    };
    setFieldValue(buttonsField, nextButtons, false);
    syncValidation(nextButtons);
  };

  const removeButton = (index) => {
    const nextButtons = getCurrentButtons();
    nextButtons.splice(index, 1);
    const normalizedNextButtons =
      nextButtons.length > 0 ? nextButtons : [createEmptyButton()];

    setFieldValue(buttonsField, normalizedNextButtons, false);
    syncValidation(normalizedNextButtons);
  };

  const addButton = () => {
    const nextButtons = [...getCurrentButtons(), createEmptyButton()];
    setFieldValue(buttonsField, nextButtons, false);
    syncValidation(nextButtons);
  };

  const renderRows = (cardBackgroundColor, rowKeyPrefix) => (
    <>
      <Stack spacing={1.25} sx={{ mt: 2 }}>
        {buttonsForView.map((button, index) => (
          <Box
            key={button._formKey}
            sx={{ display: "flex", alignItems: "center", gap: 1.25 }}
          >
            <Card
              variant="outlined"
              sx={{ p: 2, flex: 1, backgroundColor: cardBackgroundColor }}
            >
              <Grid container spacing={1.25}>
                <Grid item xs={12} md={3}>
                  <TextField
                    key={`${rowKeyPrefix}-title-${button._formKey}`}
                    label="Button title"
                    inputRef={(element) => {
                      setInputRef(`title-${index}`, element);
                    }}
                    defaultValue={buttonsForView[index]?.title || ""}
                    onBlur={(event) => {
                      updateButton(index, { title: event.target.value });
                    }}
                    className={classes.textfield}
                    InputProps={whiteInputProps}
                    sx={whiteFieldSx}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={showCommentField ? 7 : 6}>
                  <TextField
                    key={`${rowKeyPrefix}-url-${button._formKey}`}
                    label="URL"
                    inputRef={(element) => {
                      setInputRef(`url-${index}`, element);
                    }}
                    defaultValue={buttonsForView[index]?.url || ""}
                    onBlur={(event) => {
                      updateButton(index, { url: event.target.value });
                    }}
                    className={classes.textfield}
                    InputProps={whiteInputProps}
                    sx={whiteFieldSx}
                    fullWidth
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={showCommentField ? 2 : 3}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: {
                      xs: "flex-start",
                      md: "flex-end",
                    },
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!button?.open_in_popup}
                        onChange={(event) => {
                          updateButton(index, {
                            open_in_popup: event.target.checked,
                          });
                        }}
                      />
                    }
                    label="Open in a popup"
                  />
                </Grid>
                {showCommentField && (
                  <Grid item xs={12} md={7}>
                    <TextField
                      key={`${rowKeyPrefix}-comment-${button._formKey}`}
                      label="Comment for AI"
                      inputRef={(element) => {
                        setInputRef(`comment-${index}`, element);
                      }}
                      defaultValue={buttonsForView[index]?.comment_for_ai || ""}
                      onBlur={(event) => {
                        updateButton(index, {
                          comment_for_ai: event.target.value,
                        });
                      }}
                      className={classes.textfield}
                      InputProps={whiteInputProps}
                      sx={whiteFieldSx}
                      fullWidth
                    />
                  </Grid>
                )}
              </Grid>
            </Card>
            <Button
              type="button"
              variant="outlined"
              color="error"
              sx={{ width: "auto", minWidth: "unset" }}
              disabled={buttonsForView.length <= 1}
              onClick={() => removeButton(index)}
            >
              Remove
            </Button>
          </Box>
        ))}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mt: 1.5 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mt: 1.5 }}>
        <Button type="button" variant="outlined" onClick={addButton}>
          {addButtonLabel}
        </Button>
      </Box>
    </>
  );

  if (isLimitedMode) {
    return (
      <Card
        variant="outlined"
        sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}
      >
        <CardContent sx={{ pt: 2.5 }}>
          <Stack spacing={helperText ? 0.5 : 0}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0 }}>
              {sectionTitle}
            </Typography>
            {helperText ? (
              <Typography variant="body2" color="text.secondary">
                {helperText}
              </Typography>
            ) : null}
          </Stack>
          {renderRows("#fff", `limited-${keyPrefix}`)}
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Stack spacing={helperText ? 0.5 : 0}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0 }}>
            {sectionTitle}
          </Typography>
          <FormGroup row className={classes.toggle_container} sx={{ ml: 2 }}>
            {toggleLabel ? (
              <FormControlLabel
                control={
                  <Switch
                    checked={!!values?.[enabledField]}
                    onChange={(event) => {
                      const enabled = event.target.checked;
                      const nextButtons = getCurrentButtons();
                      setFieldValue(buttonsField, nextButtons, false);
                      setFieldValue(enabledField, enabled, false);
                      syncValidation(nextButtons, enabled);
                    }}
                    name={enabledField}
                    color="primary"
                    inputProps={{ "aria-label": toggleAriaLabel }}
                  />
                }
                label={toggleLabel}
              />
            ) : (
              <Switch
                checked={!!values?.[enabledField]}
                onChange={(event) => {
                  const enabled = event.target.checked;
                  const nextButtons = getCurrentButtons();
                  setFieldValue(buttonsField, nextButtons, false);
                  setFieldValue(enabledField, enabled, false);
                  syncValidation(nextButtons, enabled);
                }}
                name={enabledField}
                color="primary"
                inputProps={{ "aria-label": toggleAriaLabel }}
              />
            )}
          </FormGroup>
        </Box>
        {helperText ? (
          <Typography variant="body2" color="text.secondary">
            {helperText}
          </Typography>
        ) : null}
      </Stack>

      {values?.[enabledField] && renderRows("#f5f5f5", keyPrefix)}
    </>
  );
});

LinkButtonEditor.displayName = "LinkButtonEditor";

export default LinkButtonEditor;
